export class ArrayHelper {
  /**
   * Sorts the array by the given property.
   * 
   * @param items               The items to sort.
   * @param property            The property to use to sort.
   * @param sortType            The property to use to sort.
   * @param customPrioritizer   An optional prioritizer to apply to items.
   * 
   * @example
   * // [{ value: 1 }, { value: 2 }, { value: 3 }]
   * ArrayHelper.sortBy([{ value: 2 }, { value: 3 }, { value: 1 }], 'value');
   * 
   * @example
   * // [{ value: 12, label: 'Very important!!' }, { value: 2 }, { value: 22 }]
   * ArrayHelper.sortBy([{ value: 2 }, { value: 22 }, { value: 12, label: 'Very important!!' }], 'value', 'ascending', (item) => item.value === 12 ? -1 : null);
   * 
   * @returns A sorted array.
   */
  static sortBy<TItem, TProp extends keyof TItem>(items: TItem[], property: TProp, sortType: 'ascending' | 'descending' = 'ascending', customPrioritizer?: (item: TItem) => null | number): TItem[] {
    const smallerReturnValue = sortType === 'ascending' ? -1 : 1;
    const biggerReturnValue = sortType === 'ascending' ? 1 : -1;

    const customPriorityFunc = customPrioritizer ? customPrioritizer : () => null;

    const sorted = items.sort((a, b) => {

      const customPriority = customPriorityFunc(a);
      if (customPriority !== null) {
        return customPriority;
      }

      if (a[property] < b[property]) {
        return smallerReturnValue;
      }

      if (b[property] < a[property]) {
        return biggerReturnValue;
      }

      return 0;
    });

    return sorted;
  }

  /**
   * Adds an item to the array if the item does not exist yet.
   * 
   * @param array     The array to add the item to.
   * @param item      The item to add.
   */
  static addIfNotExists(array: any[], item: any) {
    if (this.contains(array, item)) {
      return;
    }

    array.push(item);
  }

  /**
   * Simulates the [].includes method which is included in modern browsers.
   * When we include the polyfill or can support modern ES language only, all the references to this method
   * can be replaced by the native .includes method.
   * 
   * @param array     The array to checkk.
   * @param item      The item to check.
   * 
   * @returns         true if the item is included in the array, otherwise false.
   */
  static contains(array: any[], item: any) {
    return array.indexOf(item) > -1;
  }
}
